/**
 * The browser leg of the OAuth 2.1 flow an MCP client walks, as three pure
 * functions over a query string.
 *
 * `apps/api` is the authorization server (`modules/auth/auth.ts`, `oauthPlugin`) and
 * it serves every step but two: the screen that signs a person in, and the screen
 * that asks them to approve the application. Both are addresses in this SPA, and the
 * provider gets to them by **redirecting the browser with the whole authorization
 * request in the query string, signed**.
 *
 * ## The one rule
 *
 * **The query travels verbatim.** `@better-auth/oauth-provider` signs every
 * parameter of it with the app secret (`signParams`) and re-verifies the signature
 * over every parameter but `sig` when the page hands it back
 * (`verifyOAuthQueryParams`). One parameter added, dropped, renamed or re-ordered
 * and the consent is refused with `invalid_signature` — so this file reads the query
 * as a **string** and never as the router's parsed search.
 *
 * That distinction is not a nicety, and it is the trap this file exists to keep
 * anybody from walking into: `router.state.location.searchStr` looks like the raw
 * query and is not. TanStack Router builds it by *re-stringifying* what
 * `parseSearch` decoded, and `ba_param` appears eleven times in a consent URL —
 * `qss` `decode` collapses repeats into an array and `encode` writes each key
 * **once**, so the round trip destroys the signature it is signing over. The address
 * bar is the only place the real query still exists, which is why
 * `integrations/navigate.ts` `currentSearch()` is what the route reads and why a
 * `search` string is what the page is handed.
 *
 * ## The two directions
 *
 *   - `consentRequestOf(search)` — what `/oauth/consent` shows and posts back.
 *   - `resumeAuthorizeUrl(search)` — where `/login` sends somebody afterwards when a
 *     sign-in interrupted a flow, which is `/oauth2/authorize` with the *original*
 *     request on it and the signature taken off. The provider re-validates every
 *     parameter of it against the registered client, so nothing here is trusted by
 *     anything: this only has to be faithful, not safe.
 */

import { OAUTH_AUTHORIZE_PATH, OAUTH_SIGNATURE_PARAMS } from '@fareast/shared'

/**
 * A consent request, as much of one as the screen states and the POST needs.
 *
 * `query` is the whole of what goes back to the server; the rest is what the person
 * deciding is shown, and every field of it is read out of that same string rather
 * than fetched — an application that is about to be granted a staff token should not
 * also get to choose what the screen says about it.
 */
export type ConsentRequest = {
  /** The signed query, minus the leading `?`. What `oauth_query` must be. */
  readonly query: string
  /** Who is asking. Opaque — it is whatever dynamic registration handed out. */
  readonly clientId: string
  /**
   * **Where the authorization code goes**, which is the fact this screen exists to
   * put in front of somebody.
   *
   * `.docs/reports/05-report.md` §Unauthenticated dynamic client registration: the
   * threat is not a stranger's token, it is consent phishing — anybody may register
   * a client called "Far East Studios Portal" with their own redirect URI and mail a
   * staff member the link. The name is theirs to choose and so is this, but this is
   * the one that says where the grant ends up.
   */
  readonly redirectUri: string | null
  /** What was asked for, in the order it was asked in. Often empty. */
  readonly scopes: readonly string[]
  /** RFC 8707: the audience the token will be bound to. Repeatable. */
  readonly resources: readonly string[]
}

/**
 * The consent request in a query string, or null when there is not one.
 *
 * Null is "somebody typed this address", not "something went wrong": `/oauth/consent`
 * with no query is a page nobody was sent to, and it says so rather than posting an
 * empty signature at the server to be told the same thing one round trip later.
 *
 * The two things that must be there are the signature — without it the server refuses
 * the whole request — and `client_id`, which is the one parameter `consentEndpoint`
 * reads before anything else and rejects the request for missing.
 */
export function consentRequestOf(search: string): ConsentRequest | null {
  const query = search.startsWith('?') ? search.slice(1) : search
  const params = new URLSearchParams(query)

  const clientId = params.get('client_id')
  if (clientId === null || clientId === '' || params.get('sig') === null) return null

  return {
    query,
    clientId,
    redirectUri: params.get('redirect_uri'),
    // A space-separated list, and `URLSearchParams` has already decoded the `+`.
    scopes: (params.get('scope') ?? '').split(' ').filter((scope) => scope !== ''),
    resources: params.getAll('resource'),
  }
}

/**
 * Where to send a browser once a sign-in that interrupted an OAuth flow is finished,
 * or null when nothing was interrupted.
 *
 * The provider sends a signed-out person to `/login` carrying the signed query, and
 * `routes/login.tsx` would otherwise drop it: `validateSearch` knows two keys and the
 * magic link's `callbackURL` would be `/`. Signing in would work and the client that
 * opened the browser would wait for ever.
 *
 * So the callback is `/oauth2/authorize` with the original request back on it. The
 * signature comes off because the request is no longer travelling through anything
 * that could edit it — it is going straight back to the endpoint that issued it, over
 * a session cookie, and that endpoint validates `client_id`, `redirect_uri` and PKCE
 * against the registered client itself. Leaving `exp` on would be worse than useless:
 * ten minutes is the deadline for *the browser leg*, and the person is on the other
 * side of an email round trip.
 */
export function resumeAuthorizeUrl(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  if (params.get('sig') === null) return null

  for (const name of OAUTH_SIGNATURE_PARAMS) params.delete(name)
  const request = params.toString()
  return request === '' ? null : `${OAUTH_AUTHORIZE_PATH}?${request}`
}
